import React from 'react';
import PropTypes from 'prop-types';
import CrmAvatar from './CrmAvatar';
import GivingBadge from './GivingBadge';
import { formatMoney } from '../utils/crmMoney';
import crmLabels from '../utils/crmLabels';
import '../styles/crmSearch.css';

const dueLabel = (at) => {
  if (!at) return null;
  const day = new Date(at); day.setHours(0, 0, 0, 0);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff = Math.round((day - today) / 86400000);
  if (diff < 0) return 'Overdue';
  if (diff === 0) return 'Due today';
  if (diff === 1) return 'Due tomorrow';
  return new Date(at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

function CrmSearchResults({ data, loading, term, teamId, active, setActive, go, seeAllHref, labels = crmLabels(undefined) }) {
  let cursor = -1;
  const row = (item, content) => {
    cursor += 1;
    const i = cursor;
    return (
      <div
        key={`${item.type}-${item.id}`}
        className={`crm-gsearch__row${i === active ? ' is-active' : ''}`}
        onMouseEnter={() => setActive(i)}
        onMouseDown={(e) => { e.preventDefault(); go(item.href); }}
      >
        {content}
      </div>
    );
  };

  const section = (label, nodes) => (nodes.length ? (
    <div className="crm-gsearch__section">
      <div className="crm-gsearch__section-head">{label}</div>
      {nodes}
    </div>
  ) : null);

  const contactRow = (c) => row(c, (
    <>
      <CrmAvatar firstName={c.first_name} lastName={c.last_name} name={c.name} avatarUrl={c.avatar_thumb_url} size={32} />
      <div className="crm-gsearch__main">
        <div className="crm-gsearch__title-line">
          <span className="crm-gsearch__name">{c.name}</span>
          {c.badge && <GivingBadge variant="gray">{c.badge}</GivingBadge>}
        </div>
        {c.subtitle && <div className="crm-gsearch__subtitle">{c.subtitle}</div>}
      </div>
    </>
  ));

  const orgRow = (o) => row(o, (
    <>
      <CrmAvatar name={o.name} avatarUrl={o.avatar_thumb_url} size={32} />
      <div className="crm-gsearch__main">
        <div className="crm-gsearch__name">{o.name}</div>
        {o.subtitle && <div className="crm-gsearch__subtitle">{o.subtitle}</div>}
      </div>
    </>
  ));

  const dealRow = (d) => row(d, (
    <>
      <i className="far fa-file-invoice-dollar crm-gsearch__icon" aria-hidden="true" />
      <div className="crm-gsearch__main">
        <div className="crm-gsearch__title-line">
          <span className="crm-gsearch__name">{d.name}</span>
          {d.badge && <GivingBadge variant="warning">{d.badge}</GivingBadge>}
        </div>
        {d.subtitle && <div className="crm-gsearch__subtitle">{d.subtitle}</div>}
      </div>
      {formatMoney(d.amount) && <div className="crm-gsearch__meta">{formatMoney(d.amount)}</div>}
    </>
  ));


  const taskRow = (t) => row(t, (
    <>
      <i className={`${t.done ? 'far fa-check-square' : 'far fa-square'} crm-gsearch__icon`} aria-hidden="true" />
      <div className="crm-gsearch__main">
        <div className="crm-gsearch__name">{t.name}</div>
      </div>
      {dueLabel(t.due_at) && (
        <div className={`crm-gsearch__meta${dueLabel(t.due_at) === 'Overdue' ? ' crm-gsearch__meta--warn' : ''}`}>
          {dueLabel(t.due_at)}
        </div>
      )}
    </>
  ));

  const showEmpty = !loading && term && data.total === 0;

  return (
    <div className="crm-gsearch__dropdown">
      {showEmpty ? (
        <div className="crm-gsearch__empty">
          <div className="crm-gsearch__empty-icon"><i className="far fa-search" aria-hidden="true" /></div>
          <div className="crm-gsearch__empty-title">{`No matches for “${term}”`}</div>
          <p className="crm-gsearch__empty-text">
            {`Search looks across ${labels.contact.plural.toLowerCase()}, ${labels.organization.plural.toLowerCase()}, ${labels.deal.plural.toLowerCase()}, and tasks. Try another spelling — or create a new record.`}
          </p>
          <div className="crm-gsearch__empty-actions">
            <a className="btn btn-setlist-secondary" href={`/teams/${teamId}/crm/fans?action=import`}>Import CSV</a>
            <a className="btn btn-setlist-primary" href={`/teams/${teamId}/crm/fans?action=new`}>{`New ${labels.contact.singular.toLowerCase()}`}</a>
          </div>
        </div>
      ) : (
        <>
          {loading && data.total === 0 && <div className="crm-gsearch__loading">Searching…</div>}
          {section(labels.contact.plural, data.contacts.map(contactRow))}
          {section(labels.organization.plural, data.organizations.map(orgRow))}
          {section(labels.deal.plural, data.deals.map(dealRow))}
          {section('Grants', (data.grants || []).map(dealRow))}
          {section('Tasks', data.tasks.map(taskRow))}
          {data.total > 0 && (
            <div className="crm-gsearch__footer">
              <span className="crm-gsearch__hints">↑↓ navigate · ↵ open · esc dismiss</span>
              <a href={seeAllHref}>{`See all ${data.total}${data.capped ? '+' : ''} results →`}</a>
            </div>
          )}
        </>
      )}
    </div>
  );
}

CrmSearchResults.propTypes = {
  data: PropTypes.object.isRequired,
  loading: PropTypes.bool,
  term: PropTypes.string,
  teamId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  active: PropTypes.number,
  setActive: PropTypes.func.isRequired,
  go: PropTypes.func.isRequired,
  seeAllHref: PropTypes.string,
  labels: PropTypes.object,
};

export default CrmSearchResults;
